/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {
  Avatar, Button,
  createStyles,
  FormControl, InputAdornment,
  ListItem,
  ListItemAvatar, ListItemText, Snackbar,
  StyleRules, TextField,
  Theme,
  Typography
} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import {AccountCircle, AlternateEmail, Comment, Send} from "@material-ui/icons";
import {Alert} from "@material-ui/lab";
import classNames from 'classnames';
import React, {memo, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import Carousel from "react-material-ui-carousel";
import {apiUrl} from "../../utils/api";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type CommentsClassKey = 'root' | 'heading' | 'divCoffee' | 'divider' | 'list' | 'listItem' | 'typoComment' | 'avatar' | 'divText' | 'carousel' | 'form' | 'textField' | 'btnIcon' | 'submitButton';

const useStyles = makeStyles<Theme, {}, CommentsClassKey>(
  (): StyleRules<CommentsClassKey> =>
    createStyles({
      root: {
        padding: 'clamp(48px, 10vh, 120px) clamp(20px, 4vw, 56px) clamp(60px, 10vh, 120px)'
      },
      heading: {
        margin: '0 0 clamp(24px, 4vh, 40px)',
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(30px, 4vw, 48px)',
        letterSpacing: '-.015em'
      },
      divCoffee: {
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'center'
      },
      divText: {},
      divider: {
        color: 'red',
        backgroundColor: 'yellow'
      },
      list: {
        width: '50%',
        maxHeight: '65vh',
        overflowX: 'hidden',
        overflowY: 'auto',
        marginLeft: '25%',
        paddingRight: '20px'
      },
      listItem: {
        border: '1px solid var(--color-rule)',
        borderRadius: 0,
        background: 'var(--color-surface)',
        margin: '8px',
        marginRight: '20px',
        minHeight: '150px',
        textAlign: 'center',
        alignItems: 'center'
      },
      typoComment: {
        color: 'var(--color-text)'
      },
      avatar: {
        color: 'var(--color-bg)',
        backgroundColor: 'var(--color-text)',
        borderRadius: 0
      },
      carousel: {
        width: 'calc(100% - 2 * clamp(20px, 4vw, 56px))',
        maxWidth: '560px',
        margin: '16px auto 0',
        paddingTop: '20px',
        paddingRight: '12px'
      },
      form: {
        background: '#ffffff',
        width: '50%',
        minWidth: '280px',
        padding: '20px 24px',
        margin: '20px auto 0'
      },
      textField: {
        margin: '8px 0px'
      },
      btnIcon: {
        marginRight: '12px'
      },
      submitButton: {
        marginTop: '8px',
        borderRadius: 0,
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        fontSize: '11.5px',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        padding: '12px 22px',
        '&:hover': {
          background: '#000'
        },
        // Default MUI disabled-button styling (a near-black grey text) wins the
        // specificity tie against our own color rule above, which renders it
        // invisible on top of the near-black `--color-bg` background. Override
        // it explicitly here so the label stays legible while disabled.
        '&.Mui-disabled': {
          color: 'var(--color-text-faint)'
        }
      }
    }),
  {
    name: 'Comments'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type CommentsProps = {
  className?: string;
};

export default memo(({className}: CommentsProps) => {
  // hooks
  const classes = useStyles({});
  const {t, i18n} = useTranslation('common');

  const [data, setData] = useState(null);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [openSnack, setOpenSnack] = useState<boolean>(false);


  useEffect(() => {
    fetch(apiUrl("api/getComments.php"))
    .then(response => response.json())
    .then(d => setData(d));
  }, []);

  const handleAddComment = () => {
    let formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    formData.append('comment', comment);

    fetch(apiUrl("api/addComment.php"),
    {
      body: formData,
      method: "post"
    }).then(() => setOpenSnack(true));
  };

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    setOpenSnack(false);
  };

  return (
    <div className={classNames(classes.root, className)}>
      <h2 className={classes.heading}>{t('topBarMenu.guestBook')}</h2>

      {/* "Buy me a coffee" block, disabled for now
      <div className={classes.divCoffee}>
        <div>
          <Typography className={classes.divText}>Please leave a comment below, and if you'd like to help, feel free to buy me a coffee :)</Typography>
          <Typography className={classes.divText}>After each donation goal is attained, a photography will be granted and sent to a random donator.</Typography>
        </div>
        <a href="https://www.buymeacoffee.com/pierrecastay"><img src="https://img.buymeacoffee.com/button-api/?text=Buy me a coffee&emoji=&slug=pierrecastay&button_colour=FFDD00&font_colour=000000&font_family=Bree&outline_colour=000000&coffee_colour=ffffff" /></a>
      </div>
      */}

      <FormControl className={classes.form}>
        <TextField
          className={classes.textField}
          id="input-name"
          label="Your Name"
          variant='outlined'
          onChange={(e) => setName(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AccountCircle />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          className={classes.textField}
          id="input-mail"
          label="Your email (will remain hidden)"
          variant='outlined'
          onChange={(e) => setEmail(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AlternateEmail />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          className={classes.textField}
          id="input-mail"
          label="Your comments"
          variant='outlined'
          multiline
          rows={4}
          onChange={(e) => setComment(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Comment />
              </InputAdornment>
            ),
          }}
        />
        <Button className={classes.submitButton} onClick={handleAddComment} disabled={comment.length === 0 || name.length === 0} disableRipple>
          <Send className={classes.btnIcon}/>
          <Typography>Envoyer</Typography>
        </Button>
      </FormControl>

      {/*<Container fixed>*/}
      {/*<List className={classes.list}>*/}
        {/*<Divider className={classes.divider} variant="inset" component="li" />*/}
      {/* Keyed by language, see Home.tsx for why. */}
      <Carousel key={i18n.language} className={classes.carousel} animation='fade' interval={9000} timeout={800} >
        {(data || []).map((d) => {
          const json = JSON.parse(d);
          return (
            <ListItem className={classes.listItem} alignItems="flex-start">
              <ListItemAvatar>
                <Avatar className={classes.avatar}>{(json.name || 'N A').match(/\b(\w)/g).join('').toUpperCase()}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={json.name}
                secondary={
                  <React.Fragment>
                    <Typography
                      className={classes.typoComment}
                      component="span"
                      variant="body2"
                    >
                      {json.comment}
                    </Typography>

                  </React.Fragment>
                }
              />
            </ListItem>
          );
        })}
      </Carousel>
      {/*</List>*/}
      {/*</Container>*/}
      <Snackbar open={openSnack} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{vertical: 'top', horizontal: 'center'}} >
        <Alert onClose={handleClose} severity="success">
          {t("comments.thanks")}
        </Alert>
      </Snackbar>
    </div>
  );
});
